import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { urlMyspreadApi } from '@apis/urls';
import { basicAuthConfig } from '@apis/configs';
import * as FaIcons from 'react-icons/fa';
import { useTable, usePagination, useGlobalFilter } from 'react-table';
import { Form } from 'react-bootstrap';
import SimpleMantenedorLayout from '../SimpleMantenedorLayout';
import { useUsuariosManagement } from '../../hooks/useUsuariosManagement';
import PermissionBadges from '../Usuarios/PermissionBadges';
import EditPermissionsModal from '../Usuarios/EditPermissionsModal';

// Componente de filtro global - igual que en ControlGestion
const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <input
      value={globalFilter || ''}
      onChange={e => setGlobalFilter(e.target.value || undefined)}
      placeholder="Buscar usuarios..."
      style={{
        marginBottom: '10px',
        padding: '8px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        width: '300px',
        float: 'right'
      }}
    />
  );
};

const TestUsuario = () => {
  // Usar el hook completo de gestión de usuarios
  const {
    usuarios,
    permisos,
    modulos,
    loading,
    loadingUpdate,
    error,
    updatePermission,
    canManageUsers
  } = useUsuariosManagement();

  // Estado para el modal de edición de permisos
  const [modalState, setModalState] = useState({
    isOpen: false,
    usuario: null,
    modulo: null,
    moduloData: null
  });

  // Función para manejar la actualización de permisos
  const handleUpdatePermission = async (userId, modulo, permiso, action) => {
    try {
      const result = await updatePermission(userId, modulo, permiso, action);
      if (!result.success) {
        alert(`Error al ${action === 'add' ? 'agregar' : 'eliminar'} permiso: ${result.error}`);
      }
      return result;
    } catch (error) {
      console.error('Error updating permission:', error);
      alert(`Error al ${action === 'add' ? 'agregar' : 'eliminar'} permiso`);
      return { success: false, error: error.message };
    }
  };

  // Funciones para manejar el modal
  const handleEditPermissions = (usuario, moduloName) => {
    const moduloData = modulos.find(m => m.modulo === moduloName);
    
    console.log('🔧 Abriendo modal de permisos:', {
      usuario: usuario?.nombre,
      modulo: moduloName,
      moduloData,
      todosLosPermisos: permisos?.length,
      usuarioCompleto: usuario
    });
    
    setModalState({
      isOpen: true,
      usuario,
      modulo: moduloName,
      moduloData
    });
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      usuario: null,
      modulo: null,
      moduloData: null
    });
  };

  // Configurar columnas de react-table con módulos dinámicos
  const columns = useMemo(() => {
    // Columna base de usuario
    const baseColumns = [
      {
        Header: 'Usuario',
        accessor: row => row.displayName || row.nombre || 'N/A',
        id: 'usuario',
        width: 220,
        Cell: ({ row }) => (
          <div>
            <div style={{ fontWeight: '500', marginBottom: '2px', fontSize: '14px' }}>
              {row.original.displayName || row.original.nombre || 'N/A'}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {row.original.email}
            </div>
            <div style={{ 
              fontSize: '10px', 
              color: row.original.activo ? '#28a745' : '#dc3545',
              fontWeight: '500'
            }}>
              {row.original.activo ? '● Activo' : '● Inactivo'}
            </div>
          </div>
        )
      }
    ];

    // Si no hay módulos, solo devolver las columnas base
    if (!modulos || modulos.length === 0) {
      return [
        ...baseColumns,
        {
          Header: 'ID',
          accessor: 'id',
          width: 80
        },
        {
          Header: 'Estado',
          accessor: 'activo',
          width: 100,
          Cell: ({ value }) => (
            <span style={{
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: value ? '#d4edda' : '#f8d7da',
              color: value ? '#155724' : '#721c24'
            }}>
              {value ? 'Activo' : 'Inactivo'}
            </span>
          )
        }
      ];
    }

    // Agregar columnas dinámicas para cada módulo
    const moduloColumns = modulos.map(modulo => ({
      Header: modulo.modulo.charAt(0).toUpperCase() + modulo.modulo.slice(1),
      accessor: modulo.modulo,
      id: `modulo_${modulo.modulo}`,
      width: 180,
      Cell: ({ row }) => (
        <PermissionBadges
          usuario={row.original}
          modulo={modulo.modulo}
          todosLosPermisos={permisos}
          onClick={canManageUsers() ? handleEditPermissions : null}
        />
      )
    }));

    // console.log('📊 Columnas creadas:', baseColumns.length + moduloColumns.length);
    return [...baseColumns, ...moduloColumns];
  }, [modulos, permisos, canManageUsers]);

  // Configurar react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize
  } = useTable(
    {
      columns,
      data: usuarios,
      initialState: { pageSize: 10 }
    },
    useGlobalFilter,
    usePagination
  );

  return (
    <>
      {/* CSS para la animación de loading */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
        <SimpleMantenedorLayout
        mantenedorId="test-usuario"
        title="Test Usuarios"
        subtitle="Prueba de react-table con usuarios, permisos y módulos completos"
        icon={<FaIcons.FaUsers />}
        iconColor="#28a745"
        breadcrumbs={[
          { label: 'Test Usuario' }
        ]}
      >
        {/* Loading overlay */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 2s linear infinite',
                margin: '0 auto 10px'
              }}></div>
              <p>Cargando usuarios, permisos y módulos...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#ffebee',
            borderRadius: '6px',
            marginBottom: '20px',
            border: '1px solid #f48fb1'
          }}>
            <h3 style={{ color: '#c62828', margin: '0 0 8px 0' }}>❌ Error al cargar datos</h3>
            <p style={{ margin: '0', color: '#c62828' }}>{error}</p>
          </div>
        )}

        {/* Debug Info */}
        <div style={{ 
          marginBottom: '20px', 
          padding: '15px',
          backgroundColor: '#fff3cd',
          borderRadius: '6px',
          fontSize: '12px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>🐛 Estado del Hook useUsuariosManagement:</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
            <p><strong>Usuarios:</strong> {usuarios?.length || 0}</p>
            <p><strong>Módulos:</strong> {modulos?.length || 0}</p>
            <p><strong>Permisos:</strong> {permisos?.length || 0}</p>
            <p><strong>Loading:</strong> {loading ? 'true' : 'false'}</p>
            <p><strong>Error:</strong> {error || 'null'}</p>
            <p><strong>Can manage:</strong> {canManageUsers() ? 'true' : 'false'}</p>
          </div>
          {modulos?.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <strong>Módulos disponibles:</strong> {modulos.map(m => m.modulo).join(', ')}
            </div>
          )}
        </div>

        {/* Header con controles */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '10px' 
          }}>
            <div>
              <h2 style={{ margin: '0 0 6px 0', fontSize: '1.3rem' }}>
                Gestión de Usuarios y Permisos (react-table)
              </h2>
              <p style={{ margin: '0', color: '#666', fontSize: '13px' }}>
                Total: {usuarios?.length || 0} usuarios | {modulos?.length || 0} módulos | {permisos?.length || 0} permisos
                {!canManageUsers() && ' (Solo visualización - Sin permisos de administración)'}
              </p>
            </div>
          </div>

          {/* Filtro de búsqueda */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <GlobalFilter 
              globalFilter={state.globalFilter} 
              setGlobalFilter={setGlobalFilter} 
            />
          </div>
        </div>

      {/* Tabla con react-table - misma estructura que ControlGestion */}
      <div style={{
        border: '1px solid #ddd',
        borderRadius: '4px',
        overflow: 'hidden',
        backgroundColor: 'white'
      }}>
        <div style={{
          maxHeight: '500px',
          overflowX: 'auto',
          overflowY: 'auto'
        }}>
          <table {...getTableProps()} style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()} style={{ backgroundColor: '#f8f9fa' }}>
                  {headerGroup.headers.map(column => (
                    <th 
                      {...column.getHeaderProps()} 
                      style={{ 
                        padding: '12px 8px', 
                        borderBottom: '2px solid #dee2e6',
                        textAlign: 'left',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                    >
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.length > 0 ? (
                page.map(row => {
                  prepareRow(row);
                  return (
                    <tr 
                      {...row.getRowProps()} 
                      style={{ 
                        backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : 'white',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#e9ecef'}
                      onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = row.index % 2 === 0 ? '#f9f9f9' : 'white'}
                    >
                      {row.cells.map(cell => (
                        <td 
                          {...cell.getCellProps()} 
                          style={{ 
                            padding: '10px 8px', 
                            borderBottom: '1px solid #dee2e6' 
                          }}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td 
                    colSpan={columns.length} 
                    style={{ 
                      textAlign: 'center', 
                      padding: '40px',
                      color: '#666'
                    }}
                  >
                    {loading ? 'Cargando usuarios...' : 'No se encontraron usuarios'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación - igual que ControlGestion */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '15px',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button 
            onClick={() => gotoPage(0)} 
            disabled={!canPreviousPage}
            style={{
              padding: '4px 8px',
              border: '1px solid #ccc',
              backgroundColor: canPreviousPage ? 'white' : '#f5f5f5',
              cursor: canPreviousPage ? 'pointer' : 'not-allowed',
              borderRadius: '3px'
            }}
          >
            {'<<'}
          </button>
          <button 
            onClick={() => previousPage()} 
            disabled={!canPreviousPage}
            style={{
              padding: '4px 8px',
              border: '1px solid #ccc',
              backgroundColor: canPreviousPage ? 'white' : '#f5f5f5',
              cursor: canPreviousPage ? 'pointer' : 'not-allowed',
              borderRadius: '3px'
            }}
          >
            {'<'}
          </button>
          <span style={{ 
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Página {state.pageIndex + 1} de {pageOptions.length}
          </span>
          <button 
            onClick={() => nextPage()} 
            disabled={!canNextPage}
            style={{
              padding: '4px 8px',
              border: '1px solid #ccc',
              backgroundColor: canNextPage ? 'white' : '#f5f5f5',
              cursor: canNextPage ? 'pointer' : 'not-allowed',
              borderRadius: '3px'
            }}
          >
            {'>'}
          </button>
          <button 
            onClick={() => gotoPage(pageOptions.length - 1)} 
            disabled={!canNextPage}
            style={{
              padding: '4px 8px',
              border: '1px solid #ccc',
              backgroundColor: canNextPage ? 'white' : '#f5f5f5',
              cursor: canNextPage ? 'pointer' : 'not-allowed',
              borderRadius: '3px'
            }}
          >
            {'>>'}
          </button>
        </div>

        <Form.Select
          value={state.pageSize}
          onChange={e => setPageSize(Number(e.target.value))}
          style={{
            width: 'auto',
            fontSize: '12px'
          }}
        >
          {[5, 10, 20, 50, 100].map(pageSize => (
            <option key={pageSize} value={pageSize}>
              Ver {pageSize} filas
            </option>
          ))}
        </Form.Select>
      </div>

      {/* Debug Info */}
      <div style={{ 
        marginTop: '20px', 
        padding: '12px',
        backgroundColor: '#e9ecef',
        borderRadius: '4px',
        fontSize: '11px'
      }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>📊 Información Técnica:</h4>
        <p><strong>Usuarios cargados:</strong> {usuarios.length}</p>
        <p><strong>Filas en página actual:</strong> {page.length}</p>
        <p><strong>Total páginas:</strong> {pageOptions.length}</p>
        <p><strong>Página actual:</strong> {state.pageIndex + 1}</p>
        <p><strong>Registros por página:</strong> {state.pageSize}</p>
        <p><strong>Filtro activo:</strong> {state.globalFilter || 'Ninguno'}</p>
        <p><strong>react-table funcionando:</strong> ✅ Sí</p>
        <p><strong>SCSS:</strong> ❌ No utilizado</p>
      </div>

      {/* Modal de Permisos */}
      <EditPermissionsModal
        isOpen={modalState.isOpen}
        onClose={handleCloseModal}
        usuario={modalState.usuario}
        modulo={modalState.modulo}
        moduloData={modalState.moduloData}
        todosLosPermisos={permisos}
        onUpdatePermission={handleUpdatePermission}
        loading={loadingUpdate}
      />
    </SimpleMantenedorLayout>
    </>
  );
};

export default TestUsuario;
